/**
 * Atalhos do terminal — enum de ações, mapa padrão e normalização de combo.
 * **Zero import de zod** (o `pdv-pure.ts` reexporta daqui), porque quem mais
 * precisa disto é o terminal, que não pode pagar ~60 KB gz por um enum.
 *
 * O ponto de morar aqui e não em cada app: a tela de configuração no
 * `apps/web` grava o combo e o terminal no `apps/pdv` o resolve. Se cada lado
 * normalizasse do seu jeito, o operador gravaria "Ctrl+F9" e o terminal
 * procuraria por "ctrl+F9" — um bug que não aparece em teste unitário de
 * nenhum dos dois, só no balcão.
 */

/** Formato do jsonb. Subir a versão permite migrar sem quebrar quem já gravou. */
export const PDV_SHORTCUTS_VERSION = 1;

/**
 * Ações atalháveis. **Mesma regra de contrato do `PDV_TILES`: chave nova só se
 * ADICIONA, nunca se renomeia** — renomear apaga o atalho de quem já
 * configurou, e o operador descobre isso no meio de uma fila.
 */
export const PDV_SHORTCUT_ACTIONS = [
  "help",
  "search",
  "checkout",
  "customer",
  "qty",
  "discount",
  "void_line",
  "void_sale",
  "cash_move",
  "register",
  "reprint",
  "price_check",
  "back",
  "qty_inc",
  "qty_dec",
] as const;
export type PdvShortcutAction = (typeof PDV_SHORTCUT_ACTIONS)[number];

/**
 * Padrão de fábrica. **Array de combos por ação, não combo único**: no teclado
 * ABNT2 do balcão o `+` chega como `Equal` e como `NumpadAdd`, e o operador não
 * deveria precisar saber disso pra somar quantidade.
 */
export const PDV_SHORTCUT_DEFAULTS: Readonly<Record<PdvShortcutAction, readonly string[]>> = {
  help: ["F1"],
  search: ["F2"],
  checkout: ["F3"],
  customer: ["F4"],
  qty: ["F5"],
  discount: ["F6"],
  void_line: ["F7"],
  void_sale: ["F8"],
  cash_move: ["F9"],
  register: ["F10"],
  reprint: ["F11"],
  price_check: ["F12"],
  back: ["Escape"],
  qty_inc: ["NumpadAdd", "Equal"],
  qty_dec: ["NumpadSubtract", "Minus"],
};

export type PdvShortcutCategory = "venda" | "caixa" | "navegacao";

export interface PdvShortcutMeta {
  label: string;
  category: PdvShortcutCategory;
  /** Aparece no rodapé permanente (as 6 que respondem por 90% da descoberta). */
  footer?: boolean;
  /** Pode disparar em `keydown` repetido (segurar a tecla). */
  repeatable?: boolean;
}

export const PDV_SHORTCUT_META: Readonly<Record<PdvShortcutAction, PdvShortcutMeta>> = {
  help: { label: "Ajuda", category: "navegacao", footer: true },
  search: { label: "Buscar", category: "venda", footer: true },
  checkout: { label: "Finalizar", category: "venda", footer: true },
  customer: { label: "Cliente", category: "venda" },
  qty: { label: "Quantidade", category: "venda" },
  discount: { label: "Desconto", category: "venda", footer: true },
  void_line: { label: "Remover Item", category: "venda" },
  void_sale: { label: "Cancelar Venda", category: "venda" },
  cash_move: { label: "Sangria", category: "caixa", footer: true },
  register: { label: "Caixa", category: "caixa", footer: true },
  reprint: { label: "Reimprimir", category: "caixa" },
  price_check: { label: "Consultar Preço", category: "venda" },
  back: { label: "Voltar", category: "navegacao" },
  qty_inc: { label: "Aumentar Quantidade", category: "venda", repeatable: true },
  qty_dec: { label: "Diminuir Quantidade", category: "venda", repeatable: true },
};

export const PDV_SHORTCUT_CATEGORY_LABEL: Readonly<Record<PdvShortcutCategory, string>> = {
  venda: "Venda",
  caixa: "Caixa",
  navegacao: "Navegação",
};

/** O que está gravado em `pdv_settings.shortcuts`. */
export interface PdvShortcutConfig {
  version: number;
  bindings: Partial<Record<PdvShortcutAction, string[]>>;
}

/**
 * Evento de teclado no mínimo que precisamos. Tipado estruturalmente porque
 * este pacote não depende de lib DOM — e porque assim a normalização é
 * testável sem navegador.
 */
export interface KeyComboSource {
  code: string;
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  metaKey?: boolean;
}

/**
 * Combo canônico a partir de um evento.
 *
 * **A chave é `KeyboardEvent.code`, não `.key`.** O balcão brasileiro é ABNT2 e
 * o `.key` muda com o layout: a mesma tecla física vira `;` ou `ç` dependendo
 * de onde o terminal foi instalado. O `.code` é a posição física e não mente.
 *
 * `metaKey` é normalizado como `Ctrl` de propósito: o alvo é Windows, e no
 * macOS (onde só se desenvolve) isso deixa o Cmd exercitar o mesmo binding sem
 * criar um segundo dialeto de combo pra manter.
 */
export function normalizeCombo(e: KeyComboSource): string {
  const parts: string[] = [];
  if (e.ctrlKey || e.metaKey) parts.push("Ctrl");
  if (e.altKey) parts.push("Alt");
  if (e.shiftKey) parts.push("Shift");
  parts.push(e.code);
  return parts.join("+");
}

/** Só o modificador foi pressionado: não é combo, é o operador segurando Ctrl. */
export function isModifierOnly(code: string): boolean {
  return /^(Control|Alt|Shift|Meta)(Left|Right)$/.test(code);
}

/**
 * Combo em texto legível pro `Kbd`. `KeyQ` vira `Q`, `Digit1` vira `1`,
 * `NumpadAdd` vira `+ (num)` — o operador reconhece a tecla, não o código.
 */
export function comboLabel(combo: string): string[] {
  return combo.split("+").map((p) => {
    if (p === "Ctrl" || p === "Alt" || p === "Shift") return p;
    if (p.startsWith("Key")) return p.slice(3);
    if (p.startsWith("Digit")) return p.slice(5);
    if (p.startsWith("Numpad")) {
      const n = p.slice(6);
      const simbolo: Record<string, string> = {
        Add: "+",
        Subtract: "−",
        Multiply: "×",
        Divide: "÷",
        Decimal: ",",
        Enter: "Enter",
      };
      return `${simbolo[n] ?? n} (num)`;
    }
    const nomes: Record<string, string> = {
      Escape: "Esc",
      Equal: "=",
      Minus: "−",
      Space: "Espaço",
      ArrowUp: "↑",
      ArrowDown: "↓",
      ArrowLeft: "←",
      ArrowRight: "→",
    };
    return nomes[p] ?? p;
  });
}

/**
 * Teclas que o NAVEGADOR fica: no PWA, F1, F11 e F12 não são interceptáveis e
 * não há como contornar. No Tauri elas voltam a ser do app
 * (`with_browser_accelerator_keys(false)`), então o aviso só vale para PDV com
 * `accessMode: "web"`.
 */
export const BROWSER_RESERVED_COMBOS = ["F1", "F11", "F12"] as const;

/** Aceleradores que o navegador rouba com modificador (recuperáveis no Tauri). */
export const BROWSER_RESERVED_WITH_MODIFIER = ["Ctrl+KeyR", "Ctrl+KeyP", "Ctrl+KeyF", "F5"] as const;

export function isBrowserReserved(combo: string): boolean {
  return (
    (BROWSER_RESERVED_COMBOS as readonly string[]).includes(combo) ||
    (BROWSER_RESERVED_WITH_MODIFIER as readonly string[]).includes(combo)
  );
}

/**
 * Resolve o que vale de verdade: o gravado por cima do padrão.
 *
 * A distinção que importa e que o `tiles` já adota: **ausente herda o padrão,
 * array vazio significa desabilitada**. Sem isso não haveria como desligar um
 * atalho — gravar `[]` seria indistinguível de nunca ter configurado.
 */
export function resolveBindings(
  stored: PdvShortcutConfig | null | undefined,
): Record<PdvShortcutAction, string[]> {
  const out = {} as Record<PdvShortcutAction, string[]>;
  for (const action of PDV_SHORTCUT_ACTIONS) {
    const gravado = stored?.bindings?.[action];
    out[action] = gravado === undefined ? [...PDV_SHORTCUT_DEFAULTS[action]] : [...gravado];
  }
  return out;
}

/** Índice combo → ação, que é a direção que o listener consulta. */
export function bindingIndex(
  bindings: Record<PdvShortcutAction, string[]>,
): Map<string, PdvShortcutAction> {
  const idx = new Map<string, PdvShortcutAction>();
  for (const action of PDV_SHORTCUT_ACTIONS) {
    for (const combo of bindings[action]) {
      /* Primeiro a declarar vence. Conflito é bloqueado ao SALVAR (a config
         não deixa gravar duplicado), então aqui é só uma rede de segurança
         para dados antigos — nunca uma decisão silenciosa de UX. */
      if (!idx.has(combo)) idx.set(combo, action);
    }
  }
  return idx;
}

/** Combos usados por mais de uma ação. Vazio = configuração salvável. */
export function findConflicts(
  bindings: Record<PdvShortcutAction, string[]>,
): Array<{ combo: string; actions: PdvShortcutAction[] }> {
  const porCombo = new Map<string, PdvShortcutAction[]>();
  for (const action of PDV_SHORTCUT_ACTIONS) {
    for (const combo of bindings[action]) {
      porCombo.set(combo, [...(porCombo.get(combo) ?? []), action]);
    }
  }
  return [...porCombo.entries()]
    .filter(([, acoes]) => acoes.length > 1)
    .map(([combo, actions]) => ({ combo, actions }));
}
